import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Seo } from '../components/Seo';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { RecipeCard } from '../components/RecipeCard';
import { recipes as staticRecipes, RECIPE_CATEGORIES } from '../content/recipes';

const API = process.env.REACT_APP_BACKEND_URL;

function toCard(r) {
  return {
    slug: r.slug,
    name: r.title || r.name,
    kategorie: r.kategorie,
    kurzbeschreibung: r.kurzbeschreibung,
    glutenfrei: r.glutenfrei,
    gesamtzeit: r.gesamtzeit,
    image: r.hauptbild
      ? { src: r.hauptbild.url.startsWith('/api/') ? `${API}${r.hauptbild.url}` : r.hauptbild.url, alt: r.hauptbild.alt || '' }
      : r.image,
  };
}

export default function Rezepte() {
  const [recipes, setRecipes] = useState(staticRecipes);

  useEffect(() => {
    fetch(`${API}/api/recipes`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => { if (d.recipes?.length) setRecipes(d.recipes.map(toCard)); })
      .catch(() => {});
  }, []);
  return (
    <>
      <Seo
        title="Hunderezepte für anspruchsvolle Hunde | Felix Genuss"
        description="Unsere Rezeptsammlung für Hunde, die gutes Essen zu schätzen wissen: sorgfältig ausgewählte Zutaten, nachvollziehbare Zubereitung, verständliche Informationen."
        path="/rezepte"
      />
      <Breadcrumbs items={[{ label: 'Startseite', href: '/' }, { label: 'Rezepte' }]} />

      <div className="container-site max-w-3xl pt-10">
        <p className="eyebrow">Die Rezeptküche</p>
        <h1 className="mt-3 font-serif text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl">
          Rezepte für anspruchsvolle Hunde
        </h1>
        <p className="mt-6 leading-relaxed text-brand-brown/85">
          Hier entsteht unsere Rezeptsammlung für Hunde, die gutes Essen genauso zu schätzen wissen wie ihre Menschen. Wir entwickeln Rezepte mit sorgfältig ausgewählten Zutaten, nachvollziehbarer Zubereitung und verständlichen Informationen.
        </p>
        <p className="mt-4 leading-relaxed text-brand-brown/85">
          Unsere ersten Rezepte erscheinen nach und nach – den Anfang macht Felix' Lieblingsbowl. Jede Mahlzeit ist klar gekennzeichnet, etwa ob sie glutenfrei ist, und mit ehrlichen Hinweisen zu Portionsgrössen und Verträglichkeit versehen.
        </p>
      </div>

      <section className="container-site mt-14" aria-labelledby="aktuelle-rezepte">
        <h2 id="aktuelle-rezepte" className="font-serif text-2xl font-bold sm:text-3xl">Aus Felix' Küche</h2>
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {recipes.map((r) => (
            <RecipeCard key={r.slug} recipe={r} eager />
          ))}
        </div>
      </section>

      <section className="container-site mt-16" aria-labelledby="kategorien-heading">
        <h2 id="kategorien-heading" className="font-serif text-2xl font-bold sm:text-3xl">Diese Kategorien entstehen</h2>
        <p className="mt-4 max-w-2xl leading-relaxed text-brand-brown/85">
          Unsere Rezeptwelt wächst. Diese Bereiche bereiten wir bereits vor – mit der gleichen Sorgfalt wie Felix' erste Bowl.
        </p>
        <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {RECIPE_CATEGORIES.map((cat) => (
            <li key={cat.slug} className="border border-brand-line bg-brand-beige p-6" data-testid={`rezepte-category-${cat.slug}`}>
              <h3 className="font-serif text-xl font-bold">{cat.name}</h3>
              <p className="mt-2 text-sm leading-relaxed text-brand-brown-soft">{cat.description}</p>
            </li>
          ))}
        </ul>
      </section>

      <section className="container-site mt-16 max-w-3xl" aria-labelledby="rezepte-wissen-hinweis">
        <div className="border-l-2 border-brand-gold bg-brand-beige p-6">
          <h2 id="rezepte-wissen-hinweis" className="font-serif text-xl font-bold">Gut vorbereitet in die Küche</h2>
          <p className="mt-3 text-sm leading-relaxed text-brand-brown">
            Bevor du loskochst, lohnt sich ein Blick in unser Wissen:{' '}
            <Link to="/wissen/lebensmittel" className="link-inline">welche Lebensmittel Hunde essen dürfen</Link> und{' '}
            <Link to="/wissen/portionsgroessen" className="link-inline">wie Portionsgrössen richtig bemessen werden</Link>.
          </p>
        </div>
      </section>
    </>
  );
}
